import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { maintenanceCategoriesApi } from '../buildingManagementApi';
import type { MaintenanceCategoryListItem } from '../buildingManagementTypes';
import { MaintenanceCategoryModal } from '../components/MaintenanceCategoryModal';

const SETTINGS = '/general-services/building-management/settings';

/**
 * What a maintenance item IS — reached through Building Management › Settings, never as a
 * sidebar row of its own.
 */
export function MaintenanceCategoriesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<MaintenanceCategoryListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    category: MaintenanceCategoryListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchCategories = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      maintenanceCategoriesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (category: MaintenanceCategoryListItem): MenuItem[] => [
      {
        key: `restore-${category.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'maintenance-categories.delete',
        disabledReason: category.isActive ? 'This category is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', category }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await maintenanceCategoriesApi.remove(confirming.category.id);
      else await maintenanceCategoriesApi.restore(confirming.category.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.category.name}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<MaintenanceCategoryListItem>
        module="maintenance-categories"
        breadcrumb={['General Services', 'Building Management', { label: 'Settings', to: SETTINGS }, 'Categories']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['MaintenanceCategory']}
        rowKey={(category) => category.id}
        fetch={fetchCategories}
        reloadKey={reloadKey}
        searchPlaceholder="Search categories"
        emptyMessage="No categories yet. Add the kinds of obligation the buildings carry — Permits, Elevators, Air-conditioning."
        onCreate={() => setCreating(true)}
        createPermission="maintenance-categories.create"
        onRowOpen={(category) => navigate(`${SETTINGS}/categories/${category.id}`)}
        openPermission="maintenance-categories.read"
        rowActions={rowActions}
        onEdit={(category) => setEditing(category)}
        editPermission="maintenance-categories.update"
        onDelete={(category) => setConfirming({ kind: 'delete', category })}
        deletePermission="maintenance-categories.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(category) => (category.isActive ? null : 'This category is already deleted.')}
        onClearData={() => maintenanceCategoriesApi.clear()}
        clearDataPermission="maintenance-categories.clear-data"
        otherInfo={(category) => category}
      />

      {creating || editing !== null ? (
        <MaintenanceCategoryModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete category' : 'Restore category'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`maintenance-category-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `"${confirming.category.name}" stops being offered on the item form.`
                  : `"${confirming.category.name}" is offered again on the item form.`}
              </p>

              {confirming.kind === 'delete' && confirming.category.itemCount > 0 ? (
                <p>
                  {confirming.category.itemCount} item{confirming.category.itemCount === 1 ? ' is' : 's are'} filed
                  under it. They keep naming it and keep its Near Expiration window — deleting a word does
                  not re-file what was recorded — but it cannot be chosen for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted category can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<MaintenanceCategoryListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (category) => <TextCell value={category.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (category) => <TextCell value={category.description} />,
  },
  {
    /*
      An em dash where the category sets none — it then takes the window under Settings › Aging,
      which is a different fact from a window of zero.
    */
    key: 'nearExpirationDays',
    header: 'Near Expiration (days)',
    width: 170,
    align: 'right',
    sortable: true,
    summary: true,
    render: (category) => (
      <TextCell value={category.nearExpirationDays != null ? String(category.nearExpirationDays) : null} mono />
    ),
  },
  {
    key: 'itemCount',
    header: 'Items',
    width: 90,
    align: 'right',
    // A correlated count — not sortable.
    render: (category) => <TextCell value={String(category.itemCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (category) =>
      category.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
